import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import CryptoJS from 'crypto-js';

/**
 * Parses an object into a query string.
 *
 * @remarks
 * This function takes an object as input and converts it into a query string.
 * It handles nested objects and arrays by encoding their values.
 *
 * @param payload - The object to be converted into a query string.
 * @returns A query string representation of the input object.
 * If the input object is empty or contains only null or undefined values, an empty string is returned.
 *
 * */
export function parseQueryParams(payload: Record<string, any>): string {
  if (!payload) {
    return '';
  }

  const queryParams: string[] = [];

  for (const [key, value] of Object.entries(payload)) {
    // UI-only cache metadata must never become part of the backend contract.
    if (key === 'paginationRevision') {
      continue;
    }

    // Skip null, undefined, and empty arrays
    if (
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      continue;
    }

    let encodedValue: string | number | boolean;

    // Handle array values by joining them with commas
    if (Array.isArray(value)) {
      encodedValue = value.join(','); // No need to encode individual numbers in arrays
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      // Convert numbers and booleans to string without encoding
      encodedValue = value;
    } else if (typeof value === 'string') {
      // Encode string values
      encodedValue = encodeURIComponent(value);
    } else {
      continue; // Skip unsupported types
    }

    queryParams.push(`${encodeURIComponent(key)}=${encodedValue}`);
  }

  // Return the query string, prefixed with "?" if there are any parameters
  return queryParams.length ? `?${queryParams.join('&')}` : '';
}

export const extractErrorMessageFromAPI = (
  error: FetchBaseQueryError | SerializedError | any,
): string => {
  if (!error) return '';

  const dataObj =
    'data' in error && error.data && typeof error.data === 'object'
      ? (error.data as any)
      : typeof error === 'object'
        ? error
        : null;

  const directMessage = (error as { message?: unknown })?.message;
  const nestedMessage = dataObj?.message;
  const mainMessage = directMessage ?? nestedMessage;

  // Extract structured line item error list if present (e.g. error.data.errors or error.errors)
  const rawErrors = dataObj?.errors ?? (error as any)?.errors;
  let detailedErrorsStr = '';

  if (Array.isArray(rawErrors) && rawErrors.length > 0) {
    const formattedErrors = rawErrors
      .map((errItem: any) => {
        if (typeof errItem === 'string') return errItem;
        if (errItem && typeof errItem === 'object') {
          const itemMsg = errItem.message || errItem.error || '';
          const itemNo =
            errItem.poItemNo || errItem.poItem || errItem.itemNo || '';
          const matCode = errItem.materialCode || errItem.material || '';
          const lineId = errItem.lineItemId || errItem.id || '';

          const itemDetails = [
            itemNo ? `PO Item: ${itemNo}` : '',
            matCode ? `Material: ${matCode}` : '',
            lineId ? `Line: ${lineId}` : '',
          ]
            .filter(Boolean)
            .join(', ');

          if (itemDetails && itemMsg) {
            return `• ${itemDetails} → ${itemMsg}`;
          }
          return itemMsg ? `• ${itemMsg}` : JSON.stringify(errItem);
        }
        return String(errItem);
      })
      .filter(Boolean);

    if (formattedErrors.length > 0) {
      detailedErrorsStr = formattedErrors.join('\n');
    }
  }

  let baseStr = '';
  if (typeof mainMessage === 'string') {
    baseStr = mainMessage;
  } else if (Array.isArray(mainMessage)) {
    baseStr = mainMessage
      .filter(
        (item): item is string =>
          typeof item === 'string' && item.trim().length > 0,
      )
      .join(', ');
  }

  if (baseStr && detailedErrorsStr) {
    if (detailedErrorsStr.includes(baseStr)) {
      return detailedErrorsStr;
    }
    return `${baseStr}\n${detailedErrorsStr}`;
  }

  return baseStr || detailedErrorsStr || '';
};

export const extractAttachmentUploadErrorMessage = (
  error: FetchBaseQueryError | SerializedError | any,
): string => {
  const message = extractErrorMessageFromAPI(error);
  const responseData =
    error && typeof error === 'object' && 'data' in error
      ? (error.data as { details?: unknown })
      : (error as { details?: unknown } | null);
  const details =
    responseData?.details && typeof responseData.details === 'object'
      ? (responseData.details as {
          fileName?: unknown;
          suggestion?: unknown;
        })
      : null;
  const fileName =
    typeof details?.fileName === 'string' && details.fileName.trim()
      ? `File: ${details.fileName.trim()}`
      : '';
  const suggestion =
    typeof details?.suggestion === 'string' && details.suggestion.trim()
      ? details.suggestion.trim()
      : '';

  return [message, fileName, suggestion].filter(Boolean).join('\n');
};

export const redirectToLogin = () => {
  window.location.href = '/signin';
};
